// packages/web/src/components/Layout/WeekNavigation.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, addDays, startOfWeek } from 'date-fns';
import { TimezoneSelector } from '../Common/TimezoneSelector';

const CompactNavigationBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #e5e5e5',
  minHeight: 44,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0),
    minHeight: 40,
  },
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  padding: theme.spacing(0.6, 2),
  fontSize: '0.8rem',
  minWidth: 'auto',
  height: 32,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  boxShadow: '0 2px 6px rgba(196, 214, 0, 0.2)',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 4px 10px rgba(196, 214, 0, 0.3)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    margin: 0,
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(0.5),
    '& svg': {
      fontSize: '1rem',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.75rem',
    height: 28,
  },
}));

const NavigationIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  padding: theme.spacing(0.6),
  width: 32,
  height: 32,
  borderRadius: '6px',
  boxShadow: '0 2px 6px rgba(196, 214, 0, 0.2)',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 4px 10px rgba(196, 214, 0, 0.3)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  '& svg': {
    fontSize: '1rem',
  },
  [theme.breakpoints.down('sm')]: {
    width: 28,
    height: 28,
    padding: theme.spacing(0.5),
    '& svg': {
      fontSize: '0.9rem',
    },
  },
}));

const CurrentWeek = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 700,
  color: '#1a1a1a',
  margin: theme.spacing(0, 1.5),
  textAlign: 'center',
  whiteSpace: 'nowrap',
  letterSpacing: '-0.01em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    margin: theme.spacing(0, 1),
  },
}));

const JumpToDateButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  marginLeft: theme.spacing(1.5),
  padding: theme.spacing(0.6, 2),
  fontSize: '0.8rem',
  height: 32,
  minWidth: 'auto',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  border: '2px solid #1a1a1a',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
  transition: 'all 0.2s ease',
  '& .MuiButton-startIcon': {
    margin: 0,
    marginRight: theme.spacing(0.5),
    '& svg': {
      fontSize: '0.9rem',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.75rem',
    height: 28,
    marginLeft: theme.spacing(1),
  },
}));

const JumpToDateIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  marginLeft: theme.spacing(1),
  padding: theme.spacing(0.6),
  width: 32,
  height: 32,
  borderRadius: '6px',
  border: '2px solid #1a1a1a',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
  transition: 'all 0.2s ease',
  '& svg': {
    fontSize: '0.9rem',
  },
  [theme.breakpoints.down('sm')]: {
    width: 28,
    height: 28,
    padding: theme.spacing(0.5),
  },
}));

const NavigationControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const CompactDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    border: '1px solid #e5e5e5',
  },
  '& .MuiDialogTitle-root': {
    fontSize: '1.125rem',
    fontWeight: 700,
    padding: theme.spacing(2, 2.5),
    color: '#1a1a1a',
    letterSpacing: '-0.01em',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(1.5, 2.5),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1.5, 2.5),
  },
}));

const WeekNavContent = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0, 2.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1.5),
  },
}));

const NavigationGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const TimezoneGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledDialogButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '6px',
  padding: theme.spacing(0.75, 2.5),
  fontSize: '0.875rem',
}));

const PrimaryDialogButton = styled(StyledDialogButton)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  boxShadow: '0 2px 6px rgba(196, 214, 0, 0.2)',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 4px 10px rgba(196, 214, 0, 0.3)',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#999',
  },
}));

const SecondaryDialogButton = styled(StyledDialogButton)(({ theme }) => ({
  color: '#666',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

interface WeekNavigationProps {
  currentWeek: Date;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onJumpToDate: (date: Date) => void;
  selectedTimezone?: string;
  onTimezoneChange?: (timezone: string) => void;
}

export const WeekNavigation: React.FC<WeekNavigationProps> = ({
  currentWeek,
  onPreviousWeek,
  onNextWeek,
  onJumpToDate,
  selectedTimezone,
  onTimezoneChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const weekEnd = addDays(currentWeek, 6);
  const weekDisplay = `${format(currentWeek, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
  const mobileWeekDisplay = `${format(currentWeek, 'M/d')}-${format(weekEnd, 'M/d')}`;

  const handleOpenDatePicker = () => {
    setSelectedDate(currentWeek);
    setDatePickerOpen(true);
  };

  const handleCloseDatePicker = () => {
    setDatePickerOpen(false);
    setSelectedDate(null);
  };

  const handleDateConfirm = () => {
    if (selectedDate) {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      onJumpToDate(weekStart);
    }
    handleCloseDatePicker();
  };

  return (
    <>
      <CompactNavigationBar>
        <WeekNavContent>
          <NavigationGroup>
            {isMobile ? (
              <NavigationIconButton onClick={onPreviousWeek} size="small">
                <ChevronLeftIcon />
              </NavigationIconButton>
            ) : (
              <NavigationButton onClick={onPreviousWeek} startIcon={<ChevronLeftIcon />}>
                {isTablet ? 'Prev' : 'Previous Week'}
              </NavigationButton>
            )}
            
            <CurrentWeek>
              {isMobile ? mobileWeekDisplay : weekDisplay}
            </CurrentWeek>
            
            {isMobile ? (
              <NavigationIconButton onClick={onNextWeek} size="small">
                <ChevronRightIcon />
              </NavigationIconButton>
            ) : (
              <NavigationButton onClick={onNextWeek} endIcon={<ChevronRightIcon />}>
                {isTablet ? 'Next' : 'Next Week'}
              </NavigationButton>
            )}

            {isMobile ? (
              <JumpToDateIconButton onClick={handleOpenDatePicker} size="small">
                <CalendarTodayIcon />
              </JumpToDateIconButton>
            ) : (
              <JumpToDateButton onClick={handleOpenDatePicker} startIcon={<CalendarTodayIcon />}>
                Jump to Date
              </JumpToDateButton>
            )}
          </NavigationGroup>
          
          {selectedTimezone && onTimezoneChange && (
            <TimezoneGroup>
              <TimezoneSelector
                value={selectedTimezone}
                onChange={onTimezoneChange}
                size="small"
              />
            </TimezoneGroup>
          )}
        </WeekNavContent>
      </CompactNavigationBar>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CompactDialog 
          open={datePickerOpen} 
          onClose={handleCloseDatePicker}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Jump to Date</DialogTitle>
          <DialogContent>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  variant: "outlined"
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <SecondaryDialogButton onClick={handleCloseDatePicker}>
              Cancel
            </SecondaryDialogButton>
            <PrimaryDialogButton 
              onClick={handleDateConfirm} 
              disabled={!selectedDate}
            >
              Go to Week
            </PrimaryDialogButton>
          </DialogActions>
        </CompactDialog>
      </LocalizationProvider>
    </>
  );
};